import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import type { Api } from "@reduxjs/toolkit/query";
import { locationsApiSlice } from "../api/locations/locationsApiSlice";
import {
  availabilitiesApiSlice,
  supAvailabilitiesApiSlice,
} from "../api/availabilities/availabilitiesApiSlice";
import { apartmentBookingApiSlice } from "../api/apartment/apartmentApiSlice";
import { bookingsApiSlice } from "../api/bookings/bookingsApiSlice";
import { reservationsApiSlice } from "../api/reservations/reservationsApiSlice";
import { paymentsApiSlice } from "../api/payments/paymentsApiSlice";
import { hintsApiSlice } from "../api/hints/hintsApiSlice";
import { rentalsApiSlice } from "../api/rentals/rentalsApiSlice";
import { lockersApiSlice } from "../api/lockers/lockersApiSlice";
import { issueReportApiSlice } from "../api/issueReport/issueReportApiSlice";
import { receiverApiSlice } from "../api/receiver/receiverApiSlice";
import { vouchersApiSlice } from "../api/voucher/voucherApiSlice";
import { infoApiSlice } from "../api/info/infoApiSlice";
import { commercialModelsApiSlice } from "../api/commercialModels/commercialModelsApiSlice";
import { customerAgreementsApiSlice } from "../api/customerAgreements/customerAgreementsApiSlice";
import { usageLogApiSlice } from "../api/usageLog/usageLogApiSlice";
import { accountApiSlice } from "../api/account/accountApiSlice";
import { ideaSubmissionApiSlice } from "../api/ideaSubmission/ideaSubmissionApiSlice";
import { pricePreviewApiSlice } from "../api/pricePreview/pricePreviewApiSlice";
import { roomWeekApiSlice } from "../api/roomWeek/roomWeekApiSlice";
import { categoryRoomsApiSlice } from "../api/categoryRooms/categoryRoomsApiSlice";
import {
  clearAccount,
  setAccountProfile,
} from "./slice/account/accountSlice";

/**
 * Evict the caches whose contents depend on WHO is asking, whenever the
 * account identity changes (#1926).
 *
 * RTK Query keys a cache entry on the request argument alone. The account
 * travels as a header (`X-Account-Session`), so logging in or out changes what
 * the server would answer without changing any cache key — a subscribed query
 * has no reason to refetch and keeps serving the previous identity's response.
 * Once room availability is filtered per organization, that means a member who
 * logs out keeps seeing rooms restricted to their org, and a member who logs in
 * does not see them until something else happens to refetch. This is the same
 * failure mode as the admin-ui tenant leak (#1848 → #1855,
 * `docs/architecture/tenant-scoped-caching.md`); the fix has to be an explicit
 * eviction, because the cache cannot see the header.
 *
 * Implemented as a listener on the account identity rather than as calls at
 * each login/logout site: there are five such sites today (login confirm,
 * logout, GDPR delete, the 401 expiry branch, session init) and a
 * hand-maintained list of them is exactly what drifts.
 *
 * **Not the stored session resolving at app start**, either. That is
 * `accountSessionRestored`, deliberately NOT matched below (#2793): its token
 * is already on every request (`util/axios.ts`), so a search sent while
 * `GET /me` was in flight was answered for that very identity. Evicting then
 * threw a correct, in-flight answer away — `initiate` resolved with no data
 * and no error, a deep link showed "0 gefunden", and a member's coverage
 * vanished on reload (#2764 A5). The exemption is by ACTION, not by state: a
 * real login that completes while `/me` is still running (a passkey takes
 * seconds) goes through `setAccountProfile` and must evict.
 *
 * **Only a real identity change triggers it.** Re-dispatching the same profile
 * — session init re-hydrating the profile that is already loaded, for instance
 * — must not reset anything: resetting on an unchanged value wipes in-flight
 * queries and freezes mounted hooks in `isLoading` (#1862).
 */

type AnyApiSlice = Api<any, Record<string, any>, string, string, any>;

/**
 * Caches whose response depends on the caller's identity. These are evicted on
 * every account change.
 */
export const ACCOUNT_SCOPED_API_SLICES: readonly AnyApiSlice[] = [
  // Room/venue lists and their availability: filtered by organization access.
  apartmentBookingApiSlice,
  availabilitiesApiSlice,
  // The bookings tab answers "my bookings" for an account and "this device's
  // bookings" for a guest — a different question per identity.
  bookingsApiSlice,
  // The room week view (#3036): own and own-organization bookings in detail,
  // everyone else's anonymised — a different answer per identity.
  roomWeekApiSlice,
  // The object-first room list (#3035): members-only rooms appear to their
  // organisations' members only — a different answer per identity.
  categoryRoomsApiSlice,
  // The room/venue price preview: a logged-in member is quoted their
  // customer class (ADR-0008, amendment 2026-10-22) — a different price per
  // identity, so a quote cached for a guest must not survive a login.
  pricePreviewApiSlice,
];

/**
 * Caches that are identical for every caller, so an account change is not a
 * reason to drop them. Several of them additionally hold state an in-progress
 * booking depends on (a created reservation, a payment intent, an issued
 * rental) and are keyed by an explicit id the flow carries — evicting those
 * mid-flow would break the booking a user is in the middle of, which is a
 * worse bug than the one this module exists to prevent.
 *
 * Listed explicitly rather than derived as "everything not account-scoped":
 * a derived list would silently absorb every newly registered slice as
 * account-independent, which is the unsafe default and would make the
 * classification test in `__tests__/accountScopedCaches.test.ts` pass
 * vacuously. Spelling both sides out is what makes that test a real guard.
 */
export const ACCOUNT_INDEPENDENT_API_SLICES: readonly AnyApiSlice[] = [
  locationsApiSlice,
  supAvailabilitiesApiSlice,
  reservationsApiSlice,
  paymentsApiSlice,
  hintsApiSlice,
  rentalsApiSlice,
  lockersApiSlice,
  issueReportApiSlice,
  receiverApiSlice,
  vouchersApiSlice,
  infoApiSlice,
  commercialModelsApiSlice,
  customerAgreementsApiSlice,
  usageLogApiSlice,
  // Reset on its own paths (logout, GDPR delete, session-init 401) — not from
  // here. NOT on login, and not on the base-query 401 branch, so a consumer
  // that must not show an earlier answer refetches on mount itself
  // (`MembershipsSection` does).
  accountApiSlice,
  ideaSubmissionApiSlice,
];

export const accountCacheListener = createListenerMiddleware();

accountCacheListener.startListening({
  matcher: isAnyOf(setAccountProfile, clearAccount),
  effect: async (_action, api) => {
    const previous = (api.getOriginalState() as any)?.account?.profile?.id ?? null;
    const next = (api.getState() as any)?.account?.profile?.id ?? null;
    if (previous === next) return;
    ACCOUNT_SCOPED_API_SLICES.forEach((slice) => {
      api.dispatch(slice.util.resetApiState());
    });
  },
});

export default accountCacheListener;
